import { Dialog, Link } from '@material-ui/core';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { Button, Input, Label } from 'reactstrap';
import CloseIcon from '@material-ui/icons/Close';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import { Transition } from '../../components/Common/EnhancedTableHead';

import { baseUrl, languageId } from '../../helpers/EncryptionHelper';
import { Autocomplete, TextField } from '@mui/material';
import useEnterKeyListener from '../../helpers/useEnterKeyListener';
import { InputLabel, Select, MenuItem } from '@material-ui/core';
import { globalPosCache } from './CustomTableEnglishWord';

const AddEnglishWordDialog = ({
  data,
  wordTypeArray,
  visible,
  handleClose,
  Transition,
  editEnglishWord,
}) => {
  const defaultWordRow = {
    wordType: null,
  };

  const [word, setWord] = useState('');
  const [wordType, setWordType] = useState(wordTypeArray);
  const [wordRow, setWordRow] = useState([defaultWordRow]);
  const [fetchedData, setFetchedData] = useState(null);

  function handleAddEnglishWord() {
    const posObjects = wordRow
      .map((row) => ({
        languagePartOfSpeech: row.wordType?.value || row.wordType?._id,
      }))
      .filter((pos) => pos.languagePartOfSpeech);

    axios
      .post(baseUrl + '/english-word/add', {
        english_word_name: word,
        translations: posObjects.map((pos) => ({
          language: languageId,
          partsOfSpeech: [pos],
          isactive: true,
        })),
      })
      .then((res) => {
        toastr.success('English Word Added Successfully', 'Success');
        window.location.reload(false);
      })
      .catch((error) => {
        toastr.error('English Word Add Failed', 'Error!');
      });
  }

  async function handleEditEnglishWord() {
    const wordId = data?._id || data?.english_word_id;
    try {
      // 1. Update English Word Name
      await axios.put(baseUrl + '/english-word/edit/' + wordId, {
        english_word_name: word,
      });

      // 2. Handle Translations
      const allTranslations = fetchedData?.originalTranslations || [];
      const languageTranslations = allTranslations.filter(
        (t) => (t.language?._id || t.language) === languageId
      );

      const posObjects = wordRow
        .map((row) => ({
          languagePartOfSpeech:
            row.wordType?.value ||
            row.wordType?._id ||
            row.languagePartOfSpeech?._id ||
            row.languagePartOfSpeech,
        }))
        .filter((pos) => pos.languagePartOfSpeech);

      const deletePromises = [];
      const updatePromises = [];

      // Reconcile rows with translation records
      for (const row of wordRow) {
        const posValue =
          row.wordType?.value ||
          row.wordType?._id ||
          row.languagePartOfSpeech?._id ||
          row.languagePartOfSpeech;

        if (!posValue) continue;

        const payload = {
          language: languageId,
          partsOfSpeech: [{ languagePartOfSpeech: posValue }],
          isactive: true,
        };

        if (row.translationId) {
          // Update specific existing translation
          updatePromises.push(
            axios.put(
              baseUrl +
              `/english-word/translation/edit/${wordId}/${row.translationId}`,
              payload
            )
          );
        } else {
          // Add brand new translation
          updatePromises.push(
            axios.post(
              baseUrl + `/english-word/translation/add/${wordId}`,
              payload
            )
          );
        }
      }

      // Identify translations to remove (those in original but not in current wordRow)
      const currentRowTranslationIds = new Set(
        wordRow.map((r) => r.translationId).filter((id) => id)
      );
      const translationsToRemove = languageTranslations.filter(
        (t) => !currentRowTranslationIds.has(t._id)
      );

      for (const t of translationsToRemove) {
        deletePromises.push(
          axios.delete(
            baseUrl + `/english-word/translation/remove/${wordId}/${t._id}`
          )
        );
      }

      // Execute deletions first to clear any language/word-type constraints
      await Promise.all(deletePromises);

      await Promise.all(updatePromises);

      toastr.success('English Word Edited Successfully', 'Success');
      window.location.reload(false);
    } catch (error) {
      console.error('Error in edit operation', error);
      toastr.error('English Word Edit Failed', 'Error!');
    }
  }

  function handleValidation() {
    let errorMessage;
    if (!word) {
      errorMessage = 'Please enter English Word';
    } else if (!wordRow || wordRow.length === 0 || !wordRow[0].wordType) {
      errorMessage = 'Please select at least one Word Type';
    }
    if (errorMessage) {
      toastr.error(errorMessage, 'Error!');
      return false;
    }
    return true;
  }

  useEffect(() => {
    if (data && wordTypeArray?.length > 0) {
      setWord(data.english_word_name);

      const translations = data.translations || [];

      // 1. Try to find translations for current language
      let targetTranslations = translations.filter((t) => {
        const tLang = t.language?._id || t.language?.id || t.language;
        return tLang === languageId;
      });

      // 2. Fallback: If no translations found for English, but word has SOME translations 
      // or legacy root-level partsOfSpeech, use those so the user sees something
      const hasAnyTranslation = translations.length > 0;
      const hasLegacyPos = data.partsOfSpeech && data.partsOfSpeech.length > 0;

      if (targetTranslations.length === 0 && (hasAnyTranslation || hasLegacyPos)) {
        const fallbackPos = [
          ...translations.flatMap((t) => t.partsOfSpeech || []),
          ...(data.partsOfSpeech || []),
        ];
        targetTranslations = [
          {
            language: languageId,
            partsOfSpeech: fallbackPos,
            _id: null, // Treat as a new translation record
          },
        ];
      }

      if (targetTranslations.length > 0) {
        // Collect all parts of speech
        const allPosEntries = targetTranslations.flatMap((t) =>
          (t.partsOfSpeech || []).map((pos) => ({
            ...pos,
            translationId: t._id,
          }))
        );

        const fetchedRows = allPosEntries.map((pos) => {
          const langPos = pos.languagePartOfSpeech || pos.partOfSpeech;
          const langPosId =
            langPos?._id ||
            langPos?.id ||
            (typeof langPos === 'string' ? langPos : null) ||
            pos?._id ||
            pos?.id ||
            (typeof pos === 'string' ? pos : null);

          // Find label: try wordTypeArray first, then globalPosCache
          const matchingWordType =
            (wordTypeArray || []).find((wt) => wt.value === langPosId) ||
            (globalPosCache[langPosId]
              ? { value: langPosId, label: globalPosCache[langPosId] }
              : null);

          return {
            ...pos,
            translationId: pos.translationId,
            wordType: matchingWordType || null,
          };
        });

        setWordRow(fetchedRows.length > 0 ? fetchedRows : [defaultWordRow]);
        setFetchedData({
          word: data.english_word_name,
          wordRow: fetchedRows,
          originalTranslations: translations,
        });
      } else {
        setWordRow([defaultWordRow]);
        setFetchedData({
          word: data.english_word_name,
          wordRow: [defaultWordRow],
          originalTranslations: translations,
        });
      }
    } else if (!data) {
      setWord('');
      setWordRow([defaultWordRow]);
      setFetchedData(null);
    }
  }, [data, wordTypeArray]);

  useEnterKeyListener({
    querySelectorToExecuteClick: '#submitAddWordType',
  });

  useEffect(() => {
    if (wordTypeArray) {
      setWordType(wordTypeArray);
    }
    // fetchYorubaWords();
  }, [wordTypeArray]);

  return (
    <Dialog
      open={visible}
      onClose={handleClose}
      TransitionComponent={Transition}
      maxWidth={'sm'}
      fullWidth
    >
      <div style={{ width: '100%', padding: 40 }}>
        <CloseIcon
          style={{
            position: 'absolute',
            top: 20,
            right: 20,
            cursor: 'pointer',
          }}
          onClick={handleClose}
        />
        <div className='mb-3 mt-2'>
          <Label
            className='form-label required-field'
            htmlFor='formrow-name-input'
          >
            Name
          </Label>
          <Input
            required={true}
            type='text'
            className='form-control'
            id='wordNameInput'
            value={word}
            disabled={data && !editEnglishWord}
            onChange={(e) => setWord(e.target.value)}
            onFocus={() => (window.focusedInput = 'wordNameInput')}
          />
        </div>
        {wordRow &&
          wordRow.map((row, i) => (
            <div key={i}>
              {!(data && !editEnglishWord) && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'flex-end',
                    flexDirection: 'row',
                    width: '100%',
                  }}
                >
                  <CloseIcon
                    style={{
                      top: 20,
                      right: 20,
                      cursor: 'pointer',
                      alignSelf: 'end',
                    }}
                    onClick={() => {
                      setWordRow((wordRow) => {
                        let row = wordRow;
                        if (wordRow.length > 1)
                          row = row.filter((item, index) => index !== i);
                        return row;
                      });
                    }}
                  />
                </div>
              )}
              <div className='mb-3'>
                <Label
                  className='form-label required-field'
                  htmlFor='formrow-word-input'
                >
                  Word Type
                </Label>
                <Autocomplete
                  id={`wordTypeSelectInput${i}`}
                  size='small'
                  options={wordType.filter(
                    (opt) =>
                      !wordRow.some(
                        (r, rowIndex) =>
                          rowIndex !== i && r.wordType?.value === opt.value
                      )
                  )}
                  getOptionLabel={(option) => option?.label || ''}
                  renderInput={(params) => <TextField {...params} />}
                  onChange={(_, newValue) => {
                    const newWordRow = wordRow.map((r, idx) =>
                      idx === i ? { ...r, wordType: newValue } : r
                    );
                    setWordRow(newWordRow);
                  }}
                  value={row.wordType}
                  disabled={(data && !editEnglishWord) || !!row.translationId}
                  onFocus={() =>
                    (window.focusedInput = `wordTypeSelectInput${i}`)
                  }
                />
              </div>
            </div>
          ))}
        {!(data && !editEnglishWord) && (
          <div className='actions clearfix'>
            <Link
              to='#'
              className='btn btn-secondary'
              onClick={() => {
                setWordRow([...wordRow, { wordType: null }]);
              }}
              style={{
                alignItems: 'center',
                color: 'white',
              }}
            >
              + Add More
            </Link>
          </div>
        )}
        {!(data && !editEnglishWord) && (
          <div className='mt-4'>
            <Button
              id='submitAddWordType'
              color='primary'
              variant='contained'
              className='w-md'
              onClick={() => {
                if (handleValidation()) {
                  if (data) {
                    return handleEditEnglishWord();
                  }
                  handleAddEnglishWord();
                }
              }}
            >
              Submit
            </Button>
          </div>
        )}
      </div>
    </Dialog >
  );
};



export default AddEnglishWordDialog;
